import {
  ArrowUpRight,
  BookOpenText,
  Calendar,
  Check,
  ChevronDown,
  CircleUser,
  Lightbulb,
  Network,
  Quote,
  Search as SearchIcon,
  X,
} from "lucide-react";
import { Fragment, useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/lib/AppContext";
import {
  ApiError,
  type Entity,
  type PersonCard,
  type SearchAnswer,
  type SearchResult,
  type TranscriptHit,
  type TranscriptSearchAnswer,
} from "@/lib/api";
import { cn } from "@/lib/cn";
import { entityMeta, ENTITY_TYPES } from "@/lib/entityTypes";
import { formatDate, formatTimecode } from "@/lib/format";
import {
  DATE_PRESETS,
  daysBack,
  isNarrowed,
  isoDay,
  SEARCH_MODE_LABEL,
  SEARCH_SCOPES,
  searchRequest,
  UNFILTERED,
  type SearchFilters as Filters,
} from "@/lib/searchFilters";
import { subjectKindMeta } from "@/lib/subjectKinds";

/**
 * SRCH-1/SRCH-2/SRCH-3 — the screen that answers rather than stores, over both altitudes.
 *
 * M6.1 built the box and the ranked list and deliberately left the controls out, because a
 * filter that narrows nothing and a toggle over an altitude that does not exist yet are lies
 * the user cannot see through. Both now exist, so both are here: the filter row (Anyone / Any
 * entity / Any time / Current beliefs only) and the **World view | Insights | Both** segmented
 * control the handoff draws.
 *
 * **The filters are the Server's, not this page's.** Every one of them is a predicate in the
 * `WHERE` clause of both search arms, applied before ranking — narrowing a list this page
 * already has would be a page that says "no results" for a query with hundreds of them, and it
 * is the thing SRCH-2 is most explicit about. So changing a filter re-runs the search.
 *
 * **Reset restores both altitudes**, which is why it clears the whole filter state rather than
 * setting the scope back to something: silently dropping an altitude the user never turned off
 * is the bug that makes a search box untrustworthy.
 *
 * Every result is a thing a human approved. `pending` and `discarded` insights were never
 * indexed at all (INS-3), so there is nothing here that filters them out and nothing that ever
 * should be.
 *
 * **Find in transcripts** (SRCH-4, M17.4) is the other control on this page, and deliberately a
 * separate one rather than a third scope: it finds the words as they were said or written — every
 * line, reviewed or not — and answers with quotes, never results. Keeping the two apart is what lets
 * the sentence above stay true of the search it describes. Who said it and when narrow it; the
 * entity filter, the altitudes and "current beliefs" are about what is believed, so they are not
 * offered there.
 */

type Mode = "search" | "transcripts";

export function SearchPage() {
  const { api, people, entities, settings } = useApp();

  const [query, setQuery] = useState("");
  const [answer, setAnswer] = useState<SearchAnswer | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [menu, setMenu] = useState<"person" | "entity" | "date" | null>(null);

  // The scope a reset returns to is the account's, not a constant — `default_search_scope`
  // exists for exactly this and TABLES.md says so ("resetting filters returns here, never to
  // a narrower scope"). It is `both` unless somebody deliberately made it something else.
  const unfiltered = useMemo<Filters>(
    () => ({ ...UNFILTERED, scope: settings?.defaultSearchScope ?? "both" }),
    [settings?.defaultSearchScope],
  );

  const [filters, setFilters] = useState<Filters>(unfiltered);
  const [mode, setMode] = useState<Mode>("search");
  const [quotes, setQuotes] = useState<TranscriptSearchAnswer | null>(null);

  const run = useCallback(
    async (text: string, next: Filters, as: Mode = mode) => {
      if (!text) return;

      setBusy(true);
      setProblem(null);
      try {
        if (as === "transcripts") {
          setQuotes(
            await api.searchTranscripts({
              query: text,
              personId: next.personId,
              from: next.from,
              to: next.to,
            }),
          );
        } else {
          setAnswer(await api.search(searchRequest(text, next)));
        }
      } catch (cause: unknown) {
        setProblem(cause instanceof ApiError ? cause.message : "That search could not be run.");
      } finally {
        setBusy(false);
      }
    },
    [api, mode],
  );

  /** The same words asked of the other control. What was typed stays; the answer is the other one's. */
  const switchTo = useCallback(
    (next: Mode) => {
      if (next === mode) return;
      setMode(next);
      setMenu(null);
      setProblem(null);
      if (query.trim()) void run(query.trim(), filters, next);
    },
    [filters, mode, query, run],
  );

  /** A filter change is the same question asked differently, so it re-runs the search rather
   *  than waiting for another Enter. Nothing happens before there is a query to narrow. */
  const narrow = useCallback(
    (patch: Partial<Filters>) => {
      const next = { ...filters, ...patch };
      setFilters(next);
      setMenu(null);
      if (mode === "transcripts" ? quotes : answer) void run(query.trim(), next);
    },
    [answer, filters, mode, query, quotes, run],
  );

  const reset = useCallback(() => {
    setFilters(unfiltered);
    setMenu(null);
    if (mode === "transcripts" ? quotes : answer) void run(query.trim(), unfiltered);
  }, [answer, mode, query, quotes, run, unfiltered]);

  const clear = useCallback(() => {
    setQuery("");
    setAnswer(null);
    setQuotes(null);
    setProblem(null);
  }, []);

  const person = people.find((row) => row.id === filters.personId) ?? null;
  const entity = entities.find((row) => row.id === filters.entityId) ?? null;
  const narrowed = isNarrowed(filters, unfiltered);
  const transcripts = mode === "transcripts";

  return (
    <div className="mx-auto flex max-w-[1120px] flex-col gap-[var(--space-6)] px-[var(--space-8)] py-[var(--space-8)]">
      <header className="flex flex-wrap items-baseline gap-[var(--space-3)]">
        <h1 className="text-[length:var(--text-3xl)] font-extrabold tracking-[var(--tracking-snug)] text-[var(--text-primary)]">
          Search
        </h1>
        <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">
          {transcripts
            ? "The words as they were said or written — every line, reviewed or not."
            : "Two altitudes: the beliefs you hold, and the insights they were read from."}
        </p>
      </header>

      <div
        role="tablist"
        aria-label="What to search"
        className="inline-flex w-fit gap-[2px] rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] p-[3px]"
      >
        {(
          [
            { id: "search", label: "Search", icon: SearchIcon },
            { id: "transcripts", label: "Find in transcripts", icon: Quote },
          ] as const
        ).map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={mode === tab.id}
            onClick={() => switchTo(tab.id)}
            className={cn(
              "inline-flex items-center gap-[6px] whitespace-nowrap rounded-[var(--radius-md)] px-[var(--space-3)] py-[6px] text-[length:var(--text-xs)] transition-colors",
              mode === tab.id
                ? "bg-[var(--surface-2)] font-semibold text-[var(--text-primary)]"
                : "font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
            )}
          >
            <tab.icon size={12} strokeWidth={2} aria-hidden />
            {tab.label}
          </button>
        ))}
      </div>

      <div className="relative">
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !busy) void run(query.trim(), filters);
          }}
          placeholder={transcripts ? "Words somebody said or wrote…" : "Search everything you have captured…"}
          aria-label={transcripts ? "Find in transcripts" : "Search"}
          className={cn(
            "h-[44px] w-full rounded-[var(--radius-lg)] border border-[var(--border-default)]",
            "bg-[var(--surface-glass)] pl-[var(--space-4)] pr-[42px] backdrop-blur-[var(--blur-sm)]",
            "text-[length:var(--text-sm)] text-[var(--text-primary)]",
            "placeholder:text-[var(--text-muted)] focus:border-[var(--primary)] focus:outline-none",
          )}
        />
        {query && (
          <button
            type="button"
            onClick={clear}
            aria-label="Clear the search"
            className={cn(
              "absolute right-[6px] top-1/2 grid h-[22px] w-[22px] -translate-y-1/2 place-items-center",
              "rounded-[var(--radius-full)] bg-[var(--surface-2)] text-[var(--text-secondary)]",
              "hover:text-[var(--text-primary)]",
            )}
          >
            <X size={12} strokeWidth={2} aria-hidden />
          </button>
        )}
      </div>

      {/* An invisible full-screen backdrop closes an open menu, so a click anywhere else in
          the app dismisses it without every control having to know about the others. */}
      {menu && <div className="fixed inset-0 z-[70]" onClick={() => setMenu(null)} aria-hidden />}

      <div className="flex flex-wrap items-center gap-[var(--space-3)]">
        <PersonFilter
          people={people}
          selected={person}
          open={menu === "person"}
          onToggle={() => setMenu(menu === "person" ? null : "person")}
          onPick={(personId) => narrow({ personId })}
        />
        {!transcripts && (
          <EntityFilter
            entities={entities}
            selected={entity}
            open={menu === "entity"}
            onToggle={() => setMenu(menu === "entity" ? null : "entity")}
            onPick={(entityId) => narrow({ entityId })}
          />
        )}
        <DateFilter
          from={filters.from}
          to={filters.to}
          open={menu === "date"}
          onToggle={() => setMenu(menu === "date" ? null : "date")}
          onPick={(range) => narrow(range)}
        />

        {transcripts && (
          <span className="ml-auto whitespace-nowrap text-[length:var(--text-xs)] text-[var(--text-muted)]">
            {quotes ? (quotes.results.length === 1 ? "1 quote" : `${quotes.results.length} quotes`) : ""}
          </span>
        )}

        {!transcripts && (
          <div className="inline-flex items-center gap-2">
            <button
              type="button"
              role="switch"
              aria-checked={filters.currentBeliefsOnly}
              aria-label="Current beliefs only"
              onClick={() => narrow({ currentBeliefsOnly: !filters.currentBeliefsOnly })}
              className={cn(
                "relative h-[18px] w-[34px] shrink-0 rounded-[var(--radius-full)] transition-colors",
                filters.currentBeliefsOnly ? "bg-[var(--primary)]" : "bg-[var(--surface-2)]",
              )}
            >
              <span
                className={cn(
                  "absolute top-[3px] h-3 w-3 rounded-[var(--radius-full)] bg-[var(--text-inverse)] transition-[left]",
                  filters.currentBeliefsOnly ? "left-[19px]" : "left-[3px]",
                )}
              />
            </button>
            <span className="whitespace-nowrap text-[length:var(--text-xs)] text-[var(--text-secondary)]">
              Current beliefs only
            </span>
          </div>
        )}
      </div>

      {!transcripts && (
        <div className="flex flex-wrap items-center gap-[10px]">
          <span className="text-[length:var(--text-xs)] text-[var(--text-muted)]">Show</span>
          <div
            role="group"
            aria-label="Which altitudes to search"
            className="inline-flex gap-[2px] rounded-[var(--radius-full)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] p-[2px]"
          >
            {SEARCH_SCOPES.map((scope) => (
              <button
                key={scope.id}
                type="button"
                aria-pressed={filters.scope === scope.id}
                onClick={() => narrow({ scope: scope.id })}
                className={cn(
                  "whitespace-nowrap rounded-[var(--radius-full)] px-[14px] py-1 text-[length:var(--text-xs)] transition-colors",
                  filters.scope === scope.id
                    ? "bg-[var(--primary)] font-semibold text-[var(--text-inverse)]"
                    : "font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)]",
                )}
              >
                {scope.label}
              </button>
            ))}
          </div>

          <span className="ml-auto whitespace-nowrap text-[length:var(--text-xs)] text-[var(--text-muted)]">
            {answer
              ? answer.results.length === 1
                ? "1 result"
                : `${answer.results.length} results`
              : ""}
          </span>
        </div>
      )}

      {problem && (
        <p role="alert" className="text-[length:var(--text-sm)] text-[var(--warning)]">
          {problem}
        </p>
      )}

      {busy && <Skeleton />}

      {!busy && transcripts && quotes && (
        <>
          <p className="border-b border-[var(--border-subtle)] pb-[var(--space-2)] text-[length:var(--text-xs)] text-[var(--text-muted)]">
            Quotes, word for word — including what nobody has reviewed yet. A quote is what was said, not something
            your world view holds.
          </p>

          {quotes.results.length === 0 ? (
            <div className="flex flex-col items-center gap-[10px] py-[var(--space-8)] text-center">
              <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">
                {narrowed
                  ? "Nobody said that within those filters."
                  : "Nothing said or written matches those words. Try the spelling a speaker would have used."}
              </p>
              {narrowed && (
                <button
                  type="button"
                  onClick={reset}
                  className="rounded-[var(--radius-md)] border border-[var(--border-default)] px-[14px] py-[6px] text-[length:var(--text-xs)] text-[var(--text-primary)] hover:bg-[var(--surface-2)]"
                >
                  Reset filters
                </button>
              )}
            </div>
          ) : (
            <ul className="flex flex-col">
              {quotes.results.map((hit) => (
                <li key={hit.segmentId}>
                  <QuoteRow hit={hit} query={quotes.query} />
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {!busy && !transcripts && answer && (
        <>
          <p className="border-b border-[var(--border-subtle)] pb-[var(--space-2)] text-[length:var(--text-xs)] text-[var(--text-muted)]">
            {/* All three modes are ordinary states of a working system, so the one that
                ran is named rather than hidden — an ordering nobody can account for is an
                ordering nobody trusts. */}
            {SEARCH_MODE_LABEL[answer.mode]}
          </p>

          {answer.results.length === 0 ? (
            <div className="flex flex-col items-center gap-[10px] py-[var(--space-8)] text-center">
              <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">
                {narrowed
                  ? "Nothing matches those filters."
                  : "Nothing matches that. Only insights you have approved are searchable."}
              </p>
              {narrowed && (
                <button
                  type="button"
                  onClick={reset}
                  className="rounded-[var(--radius-md)] border border-[var(--border-default)] px-[14px] py-[6px] text-[length:var(--text-xs)] text-[var(--text-primary)] hover:bg-[var(--surface-2)]"
                >
                  Reset filters
                </button>
              )}
            </div>
          ) : (
            <ul className="flex flex-col">
              {answer.results.map((result) => (
                <li key={result.id}>
                  <ResultRow result={result} />
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {!busy && !(transcripts ? quotes : answer) && !problem && (
        <p className="py-[var(--space-8)] text-center text-[length:var(--text-sm)] text-[var(--text-secondary)]">
          {transcripts ? "Type the words and press Enter." : "Type a question and press Enter."}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- the filter controls

/** The handoff's filter chip: a pill that says what it is narrowing by, tinted while it is
 *  narrowing anything. */
function Chip({
  icon: Icon,
  label,
  active,
  onToggle,
  children,
}: {
  icon: typeof CircleUser;
  label: string;
  active: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="relative">
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          "inline-flex items-center gap-[6px] whitespace-nowrap rounded-[var(--radius-full)] border px-3 py-[5px]",
          "text-[length:var(--text-xs)] font-medium transition-colors",
          active
            ? "border-[var(--vl-bridge)] bg-[color-mix(in_oklch,var(--vl-bridge)_16%,transparent)] text-[var(--text-primary)]"
            : "border-[var(--border-subtle)] text-[var(--text-secondary)] hover:border-[var(--border-strong)]",
        )}
      >
        <Icon size={12} strokeWidth={2} aria-hidden />
        <span>{label}</span>
        <ChevronDown size={12} strokeWidth={2} aria-hidden className="opacity-55" />
      </button>
      {children}
    </div>
  );
}

/** The panel a chip opens. Above the backdrop, so a click inside it does not close it. */
function Menu({ width, children }: { width: string; children: React.ReactNode }) {
  return (
    <div
      className={cn(
        "absolute left-0 top-[calc(100%+6px)] z-[80] flex flex-col gap-1 rounded-[var(--radius-lg)]",
        "border border-[var(--border-default)] bg-[color-mix(in_oklch,var(--card)_92%,transparent)] p-[6px]",
        "shadow-[var(--shadow-lg)] backdrop-blur-[var(--blur-md)]",
      )}
      style={{ width }}
    >
      {children}
    </div>
  );
}

function Option({
  label,
  count,
  selected,
  onClick,
  icon: Icon,
}: {
  label: string;
  count?: number;
  selected: boolean;
  onClick: () => void;
  icon?: typeof CircleUser;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-center gap-2 rounded-[var(--radius-md)] px-2 py-[6px] text-left text-[length:var(--text-xs)]",
        "hover:bg-[var(--surface-2)]",
        selected ? "font-semibold text-[var(--text-primary)]" : "text-[var(--text-secondary)]",
      )}
    >
      <span className="inline-flex w-[14px] shrink-0 text-[var(--vl-bridge)]">
        {selected && <Check size={12} strokeWidth={2} aria-hidden />}
      </span>
      {Icon && <Icon size={12} strokeWidth={2} aria-hidden className="shrink-0 text-[var(--vl-virtual)]" />}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {count !== undefined && (
        <span className="font-[family-name:var(--font-mono)] text-[length:var(--text-2xs)] text-[var(--text-muted)]">
          {count}
        </span>
      )}
    </button>
  );
}

/** The panel's own find-a-row input. Local to the panel and never sent anywhere — it narrows
 *  the list of people, not the search. */
function Finder({ value, onChange, placeholder }: { value: string; onChange: (next: string) => void; placeholder: string }) {
  return (
    <div className="flex items-center gap-[6px] rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] px-2 py-[5px]">
      <SearchIcon size={12} strokeWidth={2} aria-hidden className="shrink-0 text-[var(--text-muted)]" />
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="min-w-0 flex-1 bg-transparent text-[length:var(--text-xs)] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-muted)]"
      />
    </div>
  );
}

function PersonFilter({
  people,
  selected,
  open,
  onToggle,
  onPick,
}: {
  people: PersonCard[];
  selected: PersonCard | null;
  open: boolean;
  onToggle: () => void;
  onPick: (id: string | null) => void;
}) {
  const [find, setFind] = useState("");
  const shown = people.filter((row) => row.name.toLowerCase().includes(find.trim().toLowerCase()));

  return (
    <Chip icon={CircleUser} label={selected?.name ?? "Anyone"} active={selected !== null} onToggle={onToggle}>
      {open && (
        <Menu width="240px">
          <Finder value={find} onChange={setFind} placeholder="Find a person…" />
          <div className="flex max-h-[220px] flex-col gap-px overflow-y-auto">
            <Option label="Anyone" selected={selected === null} onClick={() => onPick(null)} />
            {shown.length === 0 && (
              <p className="px-2 py-[10px] text-[length:var(--text-xs)] text-[var(--text-muted)]">
                Nobody by that name.
              </p>
            )}
            {shown.map((row) => (
              <Option
                key={row.id}
                label={row.name}
                count={row.insightCount}
                selected={selected?.id === row.id}
                onClick={() => onPick(row.id)}
              />
            ))}
          </div>
        </Menu>
      )}
    </Chip>
  );
}

function EntityFilter({
  entities,
  selected,
  open,
  onToggle,
  onPick,
}: {
  entities: Entity[];
  selected: Entity | null;
  open: boolean;
  onToggle: () => void;
  onPick: (id: string | null) => void;
}) {
  const [find, setFind] = useState("");

  // Persons are the control next to this one. SRCH-2 separates them because person is the
  // filter reached for most often, not because it is a different mechanism — both match
  // through `entity_mentions`, and both are the same parameter shape on the wire.
  const matching = entities.filter(
    (row) => !row.isPerson && row.name.toLowerCase().includes(find.trim().toLowerCase()),
  );

  const groups = ENTITY_TYPES.filter((meta) => meta.type !== "person")
    .map((meta) => ({ meta, rows: matching.filter((row) => row.type === meta.type) }))
    .filter((group) => group.rows.length > 0);

  return (
    <Chip icon={Network} label={selected?.name ?? "Any entity"} active={selected !== null} onToggle={onToggle}>
      {open && (
        <Menu width="264px">
          <Finder value={find} onChange={setFind} placeholder="Find an entity…" />
          <div className="flex max-h-[264px] flex-col gap-[2px] overflow-y-auto pr-1">
            <Option label="Any entity" selected={selected === null} onClick={() => onPick(null)} />
            {groups.length === 0 && (
              <p className="px-2 py-[10px] text-[length:var(--text-xs)] text-[var(--text-muted)]">
                No entity by that name.
              </p>
            )}
            {groups.map(({ meta, rows }) => (
              <div key={meta.type} className="flex flex-col gap-px pt-1">
                <p className="px-2 pb-1 pt-[2px] text-[length:var(--text-2xs)] uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
                  {meta.plural}
                </p>
                {rows.map((row) => (
                  <Option
                    key={row.id}
                    icon={entityMeta(row.type).icon}
                    label={row.name}
                    count={row.mentionCount}
                    selected={selected?.id === row.id}
                    onClick={() => onPick(row.id)}
                  />
                ))}
              </div>
            ))}
          </div>
        </Menu>
      )}
    </Chip>
  );
}

function DateFilter({
  from,
  to,
  open,
  onToggle,
  onPick,
}: {
  from: string | null;
  to: string | null;
  open: boolean;
  onToggle: () => void;
  onPick: (range: { from: string | null; to: string | null }) => void;
}) {
  const today = isoDay(new Date());
  const label = from || to ? `${from ?? "…"} → ${to ?? today}` : "Any time";

  return (
    <Chip icon={Calendar} label={label} active={from !== null || to !== null} onToggle={onToggle}>
      {open && (
        <Menu width="280px">
          <div className="flex flex-col gap-px">
            {DATE_PRESETS.map((preset) => (
              <Option
                key={preset.id}
                label={preset.label}
                selected={preset.days === null ? from === null && to === null : from === daysBack(preset.days)}
                onClick={() =>
                  onPick(preset.days === null ? { from: null, to: null } : { from: daysBack(preset.days), to: null })
                }
              />
            ))}
          </div>

          <div className="h-px bg-[var(--border-subtle)]" />

          <div className="flex flex-col gap-[6px] px-[6px] pb-[6px] pt-[2px]">
            <span className="text-[length:var(--text-2xs)] uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
              Custom range
            </span>
            <div className="flex items-center gap-[6px]">
              <DateInput label="From" value={from} max={today} onChange={(next) => onPick({ from: next, to })} />
              <span className="text-[length:var(--text-xs)] text-[var(--text-muted)]">to</span>
              <DateInput label="To" value={to} max={today} onChange={(next) => onPick({ from, to: next })} />
            </div>
            <div className="flex items-center justify-between gap-2">
              {/* The date filter reads the source material's own date — the day of the meeting
                  or the document — and never the day it was uploaded (SRCH-2). */}
              <span className="text-[length:var(--text-2xs)] text-[var(--text-muted)]">
                On the source&apos;s own date.
              </span>
              <button
                type="button"
                onClick={() => onPick({ from: null, to: null })}
                className="rounded-[var(--radius-md)] border border-[var(--border-default)] px-[10px] py-1 text-[length:var(--text-2xs)] text-[var(--text-secondary)] hover:bg-[var(--surface-2)] hover:text-[var(--text-primary)]"
              >
                Clear
              </button>
            </div>
          </div>
        </Menu>
      )}
    </Chip>
  );
}

function DateInput({
  label,
  value,
  max,
  onChange,
}: {
  label: string;
  value: string | null;
  max: string;
  onChange: (next: string | null) => void;
}) {
  return (
    <input
      type="date"
      aria-label={label}
      value={value ?? ""}
      max={max}
      onChange={(event) => onChange(event.target.value || null)}
      className="h-[28px] min-w-0 flex-1 rounded-[var(--radius-md)] border border-[var(--input)] bg-[var(--surface-glass)] px-[6px] text-[length:var(--text-xs)] text-[var(--text-primary)] outline-none"
    />
  );
}

// ---------------------------------------------------------------- the results

function ResultRow({ result }: { result: SearchResult }) {
  const insight = result.altitude === "insight";
  const Icon = insight ? Lightbulb : BookOpenText;

  // UI-7 in both directions. An insight's link carries the insight, so the source opens on
  // the transcript with the lines it was drawn from highlighted; a section's link carries the
  // anchor, so the subject page opens at the heading rather than at the top of a long one.
  const href = insight
    ? result.sourceMaterialId
      ? `${result.sourceKind === "recording" ? "/recordings" : "/sources"}/${result.sourceMaterialId}?insight=${result.insightId}`
      : null
    : result.subjectSlug
      ? `/world-view/${result.subjectSlug}${result.sectionAnchor ? `#${result.sectionAnchor}` : ""}`
      : null;

  // The handoff's breadcrumb: what altitude this is, then how to place it. A section is
  // placed by its subject's kind and title, an insight by the source it was read from.
  const meta = insight
    ? ["Insight", result.sourceTitle, formatDate(result.sourceDate)].filter(Boolean).join(" · ")
    : ["World view", result.subjectKind ? subjectKindMeta(result.subjectKind).label : null, result.subjectTitle]
        .filter(Boolean)
        .join(" › ");

  const row = (
    <div className="group flex gap-[var(--space-3)] border-b border-[var(--border-subtle)] py-[var(--space-4)]">
      <Icon
        size={14}
        strokeWidth={2}
        aria-hidden
        className={cn("mt-[3px] shrink-0", insight ? "text-[var(--vl-virtual)]" : "text-[var(--vl-life)]")}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-[var(--space-1)]">
        <div className="flex items-start justify-between gap-[var(--space-4)]">
          {/* The handoff draws a result title in its link colour; the ported tokens have no
              `--link`, and `--vl-bridge` is what the rest of the app already uses for the
              actionable accent (SourceDetail, UploadSource). Same colour, one source. */}
          <p className="text-[length:var(--text-sm)] font-semibold text-[var(--vl-bridge)] group-hover:underline">
            {result.title}
          </p>
          <div className="flex shrink-0 items-center gap-[var(--space-2)]">
            <span
              className="text-[length:var(--text-xs)] font-semibold"
              style={{ color: insight ? "var(--vl-virtual)" : "var(--vl-life)" }}
            >
              {insight ? "Insight" : "World view"}
            </span>
            {/* The blended relevance, in mono per the handoff. It is what the results were
                ordered by, world view boost included, so the numbers read down the page. */}
            <span className="font-[family-name:var(--font-mono)] text-[length:var(--text-xs)] text-[var(--text-muted)]">
              {result.score.toFixed(2)}
            </span>
          </div>
        </div>

        {result.snippet && (
          <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">{result.snippet}</p>
        )}

        <div className="flex items-center justify-between gap-[var(--space-4)]">
          <p className="truncate text-[length:var(--text-xs)] text-[var(--text-muted)]">{meta}</p>
          {href && (
            <span className="hidden shrink-0 items-center gap-1 text-[length:var(--text-xs)] text-[var(--text-muted)] group-hover:flex">
              {insight ? "Open insight" : "Open world view"}
              <ArrowUpRight size={12} strokeWidth={2} aria-hidden />
            </span>
          )}
        </div>
      </div>
    </div>
  );

  return href ? (
    <Link to={href} className="block">
      {row}
    </Link>
  ) : (
    row
  );
}

/**
 * SRCH-4 — one quote: who said it and when in the source, the line itself with the searched words
 * marked, and where it was said. The whole row jumps into the transcript at that line — a meeting
 * or recording lights it (`?segment=`); a note or a document opens its page.
 */
function QuoteRow({ hit, query }: { hit: TranscriptHit; query: string }) {
  const spoken = hit.sourceKind === "meeting" || hit.sourceKind === "recording";
  const href = spoken
    ? `/recordings/${hit.sourceMaterialId}?segment=${hit.segmentId}`
    : `/sources/${hit.sourceMaterialId}`;

  const who = hit.speakerName ?? (hit.sourceKind === "note" ? "From a note" : spoken ? "Unattributed" : "From a document");
  const meta = ["Quote", hit.sourceTitle, formatDate(hit.sourceDate)].filter(Boolean).join(" · ");

  return (
    <Link to={href} className="block">
      <div className="group flex gap-[var(--space-3)] border-b border-[var(--border-subtle)] py-[var(--space-4)]">
        <Quote size={14} strokeWidth={2} aria-hidden className="mt-[3px] shrink-0 text-[var(--vl-bridge)]" />
        <div className="flex min-w-0 flex-1 flex-col gap-[var(--space-1)]">
          <div className="flex items-start justify-between gap-[var(--space-4)]">
            <p className="text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)]">{who}</p>
            {hit.startsAtMs !== null && (
              <span className="shrink-0 font-[family-name:var(--font-mono)] text-[length:var(--text-xs)] text-[var(--text-muted)]">
                {formatTimecode(hit.startsAtMs)}
              </span>
            )}
          </div>

          <p className="text-[length:var(--text-sm)] leading-relaxed text-[var(--text-secondary)]">
            “{markWords(hit.text, hit.matchedWords ?? typedWords(query))}”
          </p>

          <div className="flex items-center justify-between gap-[var(--space-4)]">
            <p className="truncate text-[length:var(--text-xs)] text-[var(--text-muted)]">{meta}</p>
            <span className="hidden shrink-0 items-center gap-1 text-[length:var(--text-xs)] text-[var(--text-muted)] group-hover:flex">
              {spoken ? "Open at this line" : "Open the source"}
              <ArrowUpRight size={12} strokeWidth={2} aria-hidden />
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

/**
 * The words to mark in a line. The Server says which of the line's words matched, because a search
 * finds a word's Dutch and English forms and "fiets" matches "fietsen"; the typed words are only
 * the fallback for a Server that does not say. Either way, whole words, case-insensitively.
 */
function markWords(text: string, words: string[]) {
  if (words.length === 0) return text;

  const escaped = words.map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const pattern = new RegExp(`(?<![\\p{L}\\p{N}])(${escaped.join("|")})(?![\\p{L}\\p{N}])`, "giu");
  const parts = text.split(pattern);

  return parts.map((part, index) =>
    index % 2 === 1 ? (
      <mark
        key={index}
        className="rounded-[var(--radius-sm)] bg-[color-mix(in_oklch,var(--vl-bridge)_28%,transparent)] px-[2px] text-[var(--text-primary)]"
      >
        {part}
      </mark>
    ) : (
      <Fragment key={index}>{part}</Fragment>
    ),
  );
}

/** What was typed, as words: an excluded `-word` and the `or` operator left out, quotes removed. */
function typedWords(query: string) {
  return query
    .split(/\s+/)
    .filter((word) => word.length > 0 && !word.startsWith("-"))
    .map((word) => word.replace(/["“”]/g, "").trim())
    .filter((word) => word.length > 0 && word.toLowerCase() !== "or");
}

/** The handoff's three pulsing placeholder rows, so a slow search reads as working. */
function Skeleton() {
  return (
    <ul className="flex flex-col" aria-hidden>
      {[0, 1, 2].map((row) => (
        <li key={row} className="flex flex-col gap-[var(--space-2)] border-b border-[var(--border-subtle)] py-[var(--space-4)]">
          <div className="h-[14px] w-[60%] animate-pulse rounded-[var(--radius-sm)] bg-[var(--surface-2)]" />
          <div className="h-[12px] w-[38%] animate-pulse rounded-[var(--radius-sm)] bg-[var(--surface-2)]" />
        </li>
      ))}
    </ul>
  );
}
